import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../api/client";
import { Loader2, FileText, Edit, Download, Lock, User, Phone, GraduationCap, MapPin } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useAuth } from "../../context/AuthContext";
import Loader from "../../components/ui/Loader";



export default function ApplierProfileView() {
 const navigate = useNavigate();
 const { user } = useAuth();
 const [profile, setProfile] = useState(null);
 const [loading, setLoading] = useState(true);

 // Check if user is Telegram user
 const isTelegramUser = user?.telegramId || window.Telegram?.WebApp?.initDataUnsafe?.user;

 // Proper download function using fetch
 const handleDownload = async (resumeUrl) => {
 try {
 const cleanResumeUrl = resumeUrl.startsWith('/') ? resumeUrl.substring(1) : resumeUrl;
 const fullUrl = `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/${cleanResumeUrl}`;
 
 // Fetch the file
 const response = await fetch(fullUrl);
 
 if (!response.ok) {
 throw new Error(`Failed to download: ${response.statusText}`);
 }
 
 // Get the blob data
 const blob = await response.blob();
 
 // Create filename using user's full name
 let username = profile?.fullName || "resume";
 // Remove any invalid characters for filename
 username = username.replace(/[^a-zA-Z0-9]/g, '_');
 
 // Get file extension from response headers or URL
 const contentDisposition = response.headers.get('content-disposition');
 let fileExtension = 'pdf';
 let suggestedFilename = `${username}_resume.${fileExtension}`;
 
 if (contentDisposition) {
 const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
 if (filenameMatch && filenameMatch[1]) {
 suggestedFilename = filenameMatch[1];
 // If we get a filename from server, still prepend username
 const extension = suggestedFilename.split('.').pop();
 suggestedFilename = `${username}_resume.${extension}`;
 }
 } else {
 // Try to get extension from URL
 const urlParts = resumeUrl.split('.');
 if (urlParts.length > 1) {
 fileExtension = urlParts[urlParts.length - 1];
 suggestedFilename = `${username}_resume.${fileExtension}`;
 }
 }
 
 // Create a download link
 const url = window.URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = url;
 link.download = suggestedFilename;
 document.body.appendChild(link);
 link.click();
 
 // Cleanup
 document.body.removeChild(link);
 window.URL.revokeObjectURL(url);
 
 } catch (error) {
 console.error('Download failed:', error);
 
 // Fallback method - this will work for some file types
 try {
 const cleanResumeUrl = resumeUrl.startsWith('/') ? resumeUrl.substring(1) : resumeUrl;
 const fullUrl = `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/${cleanResumeUrl}`;
 let username = profile?.fullName || "resume";
 username = username.replace(/[^a-zA-Z0-9]/g, '_');
 const urlParts = resumeUrl.split('.');
 const fileExtension = urlParts.length > 1 ? urlParts[urlParts.length - 1] : 'pdf';
 const filename = `${username}_resume.${fileExtension}`;
 
 const link = document.createElement('a');
 link.href = fullUrl;
 link.download = filename;
 link.target = '_blank';
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 } catch (fallbackError) {
 console.error('Fallback download also failed:', fallbackError);
 alert('Failed to download file. Please try again.');
 }
 }
 };

 useEffect(() => {
 const fetchProfile = async () => {
 try {
 const res = await api.get("/applier/profile");
 setProfile(res.data);
 } catch (err) {
 console.error("Failed to load profile:", err);
 } finally {
 setLoading(false);
 }
 };
 fetchProfile();
 }, []);

 if (loading) return <Loader />;

 if (!profile) {
 return (
 <div className="min-h-screen flex flex-col items-center justify-center px-4">
 <div className="text-center space-y-4">
 <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto">
 <User className="w-8 h-8 text-muted-foreground" />
 </div>
 <p className="text-lg font-medium text-foreground">
 No profile data found.
 </p>
 <Link to="/applier/profile-form">
 <Button className="gap-2">
 <Edit className="w-4 h-4" />
 Create Profile
 </Button>
 </Link>
 </div>
 </div>
 );
 }

 return (
 <div className="applier-page">
 <div className="applier-page-inner max-w-4xl">
 {/* Header Section */}
 <div className="text-center space-y-4">
 <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-primary to-accent shadow-lg">
 <span className="text-3xl sm:text-4xl font-bold text-white">
 {profile.fullName ? profile.fullName.charAt(0).toUpperCase() : "U"}
 </span>
 </div>
 <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-2">
 {profile.fullName ? (
 <>
 {profile.fullName.split(" ")[0]}{" "}
 <span className="text-brand-gradient">
 {profile.fullName.split(" ").slice(1).join(" ") || ""}
 </span>
 </>
 ) : (
 "Your Profile"
 )}
 </h1>
 <p className="text-sm sm:text-base text-muted-foreground">
 Manage your personal information and application materials
 </p>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
 {/* Main Info Card */}
 <div className="lg:col-span-2 space-y-6">
 {/* Personal Information */}
 <Card className="border-2">
 <CardContent className="p-6">
 <div className="flex items-center gap-3 mb-6">
 <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
 <User className="w-5 h-5 text-primary" />
 </div>
 <h2 className="text-xl font-bold text-foreground">Personal Information</h2>
 </div>
 
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
 <ProfileItem label="Full Name" value={profile.fullName} icon={User} />
 <ProfileItem label="Gender" value={profile.gender} icon={User} />
 <ProfileItem label="Phone" value={profile.phone || "Not provided"} icon={Phone} />
 {profile.telegramUsername && (
 <ProfileItem label="Telegram" value={profile.telegramUsername} icon={Phone} />
 )}
 </div>
 </CardContent>
 </Card>

 {/* Education Section */}
 <Card className="border-2">
 <CardContent className="p-6">
 <div className="flex items-center gap-3 mb-6">
 <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
 <GraduationCap className="w-5 h-5 text-primary" />
 </div>
 <h2 className="text-xl font-bold text-foreground">Academic Records</h2>
 </div>
 
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
 <ProfileItem label="University" value={profile.university || "Not provided"} icon={GraduationCap} />
 <ProfileItem label="Education" value={profile.education || "Not provided"} icon={GraduationCap} />
 <ProfileItem label="GPA" value={profile.gpa ?? "N/A"} icon={GraduationCap} />
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Sidebar Actions & Resume */}
 <div className="space-y-6">
 {/* Resume Card */}
 <Card className="border-2 border-primary/20">
 <CardContent className="p-6">
 <div className="flex items-center gap-2 mb-4">
 <FileText className="w-5 h-5 text-primary" />
 <h3 className="font-bold text-lg">Resume</h3>
 </div>
 
 {profile.resumeUrl ? (
 <div className="space-y-3">
 <p className="text-sm text-muted-foreground">Your resume is ready for employers</p>
 <div className="flex flex-col gap-2">
 <Button
 variant="default"
 size="sm"
 className="w-full gap-2"
 onClick={() => window.open(
 `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/${profile.resumeUrl.startsWith('/') ? profile.resumeUrl.substring(1) : profile.resumeUrl}`,
 '_blank'
 )}
 >
 <FileText className="w-4 h-4" />
 View Resume
 </Button>
 <Button
 variant="outline"
 size="sm"
 className="w-full gap-2"
 onClick={() => handleDownload(profile.resumeUrl)}
 >
 <Download className="w-4 h-4" />
 Download
 </Button>
 </div>
 </div>
 ) : (
 <div>
 <p className="text-sm text-muted-foreground mb-3">No resume uploaded yet</p>
 <Link to="/applier/profile-form">
 <Button variant="default" size="sm" className="w-full">
 Upload Now
 </Button>
 </Link>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Actions Card */}
 <Card className="border-2">
 <CardContent className="p-6 space-y-3">
 <Link to="/applier/profile-form">
 <Button className="w-full gap-2" size="lg">
 <Edit className="w-4 h-4" />
 Edit Profile
 </Button>
 </Link>
 
 {!isTelegramUser && (
 <Link to="/change-password">
 <Button variant="outline" className="w-full gap-2" size="lg">
 <Lock className="w-4 h-4" />
 Change Password
 </Button>
 </Link>
 )}
 </CardContent>
 </Card>
 </div>
 </div>
 </div>
 </div>
 );
}

function ProfileItem({ label, value, icon: Icon }) {
 return (
 <div className="space-y-2">
 <div className="flex items-center gap-2">
 {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
 <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
 {label}
 </p>
 </div>
 <p className="text-base font-medium text-foreground">
 {value}
 </p>
 </div>
 );
}